import React, { useState, useEffect, useCallback } from "react";
import { Pagination, Row, Col, Collapse, Table, Tag, Alert, Button, Modal, message } from "antd";
import axios from "axios";
import PageHeader from "../Layout/PageHeader";
import PointHistoryFilters from "./PointHistoryFilters";
import { CaretRightOutlined, DownloadOutlined } from "@ant-design/icons";
import { formatDateTime } from "../../helpers/date";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

// The ledger only has two stored types (EARN / REDEEM); refunds from a cancelled redemption are
// EARN rows tagged in their remarks ("Refund:"/"Rollback:"), and expiries are REDEEM rows
// ("Points expired"). Surface those as distinct tags so a refund/cancel is visible at a glance
// instead of looking like an ordinary earn.
const renderTypeTag = (type, remarks = "") => {
  const r = (remarks || "").toLowerCase();
  if (
    type === "EARN" &&
    (r.startsWith("refund") || r.startsWith("rollback") || r.startsWith("reaper refund"))
  ) {
    return <Tag color="blue">REFUND</Tag>;
  }
  if (type === "REDEEM" && r.startsWith("points expired")) {
    return <Tag color="orange">EXPIRED</Tag>;
  }
  return <Tag color={type === "EARN" ? "green" : "red"}>{type}</Tag>;
};

const columns = [
  {
    title: "User",
    key: "user",
    width: 240,
    // The API returns an included `user` pointer object, not a flat userId field —
    // show the email (linked to the user page by objectId).
    render: (_, r) => {
      const id = r.user?.objectId;
      const email = r.user?.email;
      return id ? <a href={`/user/${id}`}>{email || id}</a> : "-";
    },
  },
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
    width: 100,
    render: (text, record) => renderTypeTag(text, record.remarks),
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    width: 100,
  },
  {
    title: "Remarks",
    dataIndex: "remarks",
    key: "remarks",
  },
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 180,
    render: (text) => (text ? formatDateTime(text) : "-"),
  },
];

const PointHistory = () => {
  const { permissions } = usePermissions();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
  });
  const [pointHistory, setPointHistory] = useState([]);
  const [filters, setFilters] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Filter params shared by the list fetch and the CSV export, so "what you see" == "what you export".
  const buildFilterParams = useCallback(
    () => ({
      ...(filters.userEmail && { userEmail: filters.userEmail }),
      ...(filters.type && { type: filters.type }),
      // Clone before startOf/endOf — moment mutates in place, which would corrupt the
      // moment instances held in filters state (and bound to the RangePicker value).
      ...(filters.startDate && {
        startDate: filters.startDate.clone().startOf("day").toISOString(),
      }),
      ...(filters.endDate && {
        endDate: filters.endDate.clone().endOf("day").toISOString(),
      }),
    }),
    [filters],
  );

  const fetchPointHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        ...buildFilterParams(),
        limit: pagination.limit,
        page: pagination.currentPage,
      };

      const res = await axios.get("/api/point-history", { params });
      setPointHistory(res.data.data);
      setPagination((prev) => ({
        ...prev,
        totalSize: res.data.pagination?.totalSize,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, buildFilterParams]);

  useEffect(() => {
    fetchPointHistory();
  }, [fetchPointHistory]);

  const handlePageChange = (currentPage, limit) => {
    setPagination((prev) => ({ ...prev, currentPage, limit }));
  };

  // Reset to page 1 whenever filters change so a stale page doesn't show an empty table.
  const handleFiltersChange = (next) => {
    setFilters(next);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Async CSV export of the point history, honouring the current filters. The backend queues the job
  // and surfaces the download link under System -> Jobs (no email) — mirrors the Redemption export.
  const handleExport = () => {
    Modal.confirm({
      title: "Download Point History?",
      content:
        "Export the point history as CSV with the current filters applied. The download link will appear under System -> Jobs once processing completes.",
      async onOk() {
        try {
          await axios.get("/api/point-history/export", { params: buildFilterParams() });
          message.info("Please check the Export progress in System -> Jobs");
        } catch (error) {
          message.error(error.response?.data?.message || error.message);
        }
      },
    });
  };

  return (
    <div>
      <PageHeader
        title="Point History"
        childRoutes={[
          { path: "/point-history", breadcrumbName: "Point History" },
        ]}
      />

      {permissions?.includes(PERMISSIONS.EXPORT_POINT_HISTORY_LIST) && (
        <Row justify="end" style={{ marginBottom: 16 }}>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Export CSV
          </Button>
        </Row>
      )}

      <Collapse
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Collapse.Panel header="Filters" key="filters">
          <PointHistoryFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />
        </Collapse.Panel>
      </Collapse>

      <Alert
        type="info"
        showIcon
        style={{ margin: "16px 0" }}
        message="Point type legend & outstanding formula"
        description={
          <span>
            The ledger stores two types — <b>EARN</b> and <b>REDEEM</b>. <b>REFUND</b> rows are EARN
            whose remarks start with refund/rollback/reaper refund; <b>EXPIRED</b> rows are REDEEM
            carrying an expiry link. Outstanding points = (Earn + Refund) − (Redeem + Expired) = Σ
            EARN − Σ REDEEM = the sum of every user's point balance.
          </span>
        }
      />

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={pointHistory}
        pagination={false}
        loading={isLoading}
        scroll={{ x: true }}
        locale={{ emptyText: "Not available" }}
      />

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination.limit}
            current={pagination.currentPage}
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onChange={handlePageChange}
          />
        </Col>
      </Row>
    </div>
  );
};

export default PointHistory;
